// CreativeWidget — renders inline in chat when Vella generates an image
// or video via generate_image / generate_video / higgsfield_generate.
//
// data shape: { url, model, type, aspect_ratio?, post_type?, creative_type?, caption? }
//   url           — the hosted media URL or data: URI
//   model         — backend identifier ("gpt_image_1_5", "higgsfield", etc.) — NOT shown
//   type          — "image" | "video"
//   aspect_ratio  — "1:1" | "4:5" | "9:16" | "16:9" (drives container ratio)
//   caption       — optional pre-generated caption text
//
// Defensive: silently no-ops on missing url so a broken backend payload
// doesn't crash the message thread.
import React from 'react';
import { TEXT, TEXT2, BORDER, FONT, DANGER } from '../../shared';
import isFailedAsset from '../../lib/isFailedAsset';

const ASPECT_TO_PADDING = {
  '1:1':  '100%',
  '4:5':  '125%',
  '9:16': '177.78%',
  '16:9': '56.25%',
  '1.91:1': '52.36%',
};

export default function CreativeWidget({ data, onAction }) {
  if (!data) return null;

  // Failed generations come back as placehold.co stand-ins or a structured
  // error. Render a real error card — never present them as usable creative.
  if (isFailedAsset(data.url, data.error)) {
    return (
      <div style={{
        width: '100%',
        maxWidth: 480,
        borderRadius: 14,
        border: `1px solid ${DANGER}55`,
        background: `${DANGER}0D`,
        fontFamily: FONT,
        padding: '16px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
          background: `${DANGER}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DANGER} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="13" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 2 }}>
            Couldn't generate
          </div>
          <div style={{ fontSize: 12.5, color: TEXT2, lineHeight: 1.45 }}>
            The creative didn't come back. Try again.
          </div>
        </div>
        {typeof onAction === 'function' && (
          <button
            type="button"
            onClick={() => onAction({ type: 'action', action: 'regenerate' })}
            style={{
              flexShrink: 0,
              fontSize: 12.5, fontWeight: 700, color: '#fff',
              padding: '8px 16px', borderRadius: 999, border: 'none',
              background: DANGER, cursor: 'pointer', fontFamily: FONT,
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const isVideo = data.type === 'video';
  const aspectPad = ASPECT_TO_PADDING[data.aspect_ratio] || '100%';

  return (
    <div style={{
      width: '100%',
      maxWidth: 480,
      borderRadius: 14,
      overflow: 'hidden',
      border: `1px solid ${BORDER}`,
      background: 'rgba(255,255,255,0.78)',
      fontFamily: FONT,
    }}>
      {/* Media — aspect-ratio container avoids layout jump while loading */}
      <div style={{ position: 'relative', width: '100%', paddingBottom: aspectPad, background: '#0f172a' }}>
        {isVideo ? (
          <video
            src={data.url}
            controls
            playsInline
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <img
            src={data.url}
            alt={data.caption || 'Generated creative'}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </div>

      {/* Caption + actions */}
      {(data.caption || onAction) && (
        <div style={{ padding: '12px 14px' }}>
          {data.caption && (
            <p style={{ fontSize: 13, color: TEXT, margin: '0 0 10px', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
              {data.caption}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a
              href={data.url}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 12, fontWeight: 600, color: TEXT, textDecoration: 'none',
                padding: '6px 12px', borderRadius: 999, border: `1px solid ${BORDER}`,
                background: 'rgba(255,255,255,0.95)',
              }}
            >
              Open full size
            </a>
            {typeof onAction === 'function' && (
              <button
                type="button"
                onClick={() => onAction({ type: 'use_creative', data })}
                style={{
                  fontSize: 12, fontWeight: 600, color: '#fff',
                  padding: '6px 12px', borderRadius: 999, border: 'none',
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)', cursor: 'pointer',
                  fontFamily: FONT,
                }}
              >
                Use this →
              </button>
            )}
          </div>
        </div>
      )}

      {data.caption || onAction ? null : (
        <div style={{ padding: '8px 12px', fontSize: 11, color: TEXT2 }}>
          Tap to expand · {data.aspect_ratio || ''}
        </div>
      )}
    </div>
  );
}
